import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Button,
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  freightServiceOptions,
  lineItemAudienceOptions,
  lineItemTypeOptions,
  markupCategoryOptions,
} from '@pelicargo/utils';
import { useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { HiPlus, HiTrash } from 'react-icons/hi2';

import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleInput } from '../SimpleInput';
import { SimpleNumber } from '../SimpleNumber';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleSelect } from '../SimpleSelect';

type MarkupCostProps = {
  prefix: string;
  onRemove: () => void;
};

const MarkupCost = ({ prefix, onRemove }: MarkupCostProps) => {
  return (
    <Tr p="0">
      <Td p="0">
        <HStack spacing="0">
          <SimpleNumber
            name={`${prefix}.min`}
            px="0"
            inputAttrs={{
              borderRadius: 0,
              textAlign: 'right',
              pr: 2,
              bg: 'none',
            }}
          />{' '}
          <Text pr="2">kg</Text>
        </HStack>
      </Td>
      <Td p="0">
        <HStack spacing="0">
          <SimpleNumber
            name={`${prefix}.cost`}
            px="0"
            inputAttrs={{
              borderRadius: 0,
              textAlign: 'right',
              pr: 2,
              bg: 'none',
            }}
          />{' '}
          <Text pr="2">$/kg</Text>
        </HStack>
      </Td>
      <Td p="0">
        <IconButton
          size="sm"
          aria-label="Remove Row"
          colorScheme="red"
          variant="ghost"
          borderRadius="none"
          icon={<Icon as={HiTrash} fontSize={16} />}
          onClick={onRemove}
        />
      </Td>
    </Tr>
  );
};

const MarkupForm = ({ prefix }: { prefix: `markups.${number}` }) => {
  const index = Number(prefix.split('.').pop());
  const markup = useWatch({ name: prefix });
  // `remove` splices; `unregister` would leave a hole in the array that zodResolver
  // rejects before the request is ever made.
  const { fields, append, remove } = useFieldArray({
    name: `${prefix}.costs`,
  });
  const { errors } = useFormState({ name: prefix });

  const isLineItemTypesEmpty = !markup?.line_item_types?.length;
  // The markups list isn't rendered through SimpleFieldArray's accordion mode, so its
  // error badge never fires. Surface errors here instead, or a collapsed markup with an
  // invalid field silently blocks the whole save.
  const hasError = !!(errors?.markups as any)?.[index];

  return (
    <AccordionItem key={prefix} my="1" borderWidth="1px" borderRadius="md" bg="bg.canvas">
      <AccordionButton justifyContent="space-between">
        <HStack spacing="2">
          <Text>{markup?.label || '[New Markup]'}</Text>
          <Show if={hasError}>
            <Badge colorScheme="red">Oops, this has some errors</Badge>
          </Show>
        </HStack>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel overflowY="auto">
        <Stack spacing="2">
          <SimpleInput name={`${prefix}.label`} label="Label" placeholder="Exchange" />
          <Stack direction="row" spacing="2">
            <SimpleSelect name={`${prefix}.category`} label="Category" options={markupCategoryOptions} />
            <SimpleReactSelect
              name={`${prefix}.applicable_freight_services`}
              label="Applicable Freight Services"
              options={freightServiceOptions}
              isMulti
            />
          </Stack>
          <Stack borderWidth={1} p={4} borderRadius="md">
            <Heading size="sm">Visibility</Heading>
            <Text fontSize="sm" color="text.muted">
              Markup(s) will generate new line items. The below controls how these line items are bundled into the
              parent line item, itemized for or excluded from bundling for specific audiences.
            </Text>
            <Show if={isLineItemTypesEmpty}>
              <Alert status="warning">
                <AlertIcon />
                <Stack spacing={0}>
                  <AlertTitle>Visible for everyone!</AlertTitle>
                  <AlertDescription>
                    This will be added as a new line item visible to all users. The audience itemizization or exclusion
                    fields are ignored if the markup is not a child line item.
                  </AlertDescription>
                </Stack>
              </Alert>
            </Show>
            <Divider mt={2} mb={2} />
            <SimpleReactSelect
              name={`${prefix}.line_item_types`}
              label="Bundled into"
              optionGroupOptions={lineItemTypeOptions?.map((group) => ({
                label: group.groupLabel,
                options: group.options.map((option) => ({ label: option.label, value: option.value })),
              }))}
              isMulti
            />
            <Text fontSize="sm" color="text.muted">
              This markup will create a child line item that lives under the parent line item (by type) on the quote
              variant. If none is selected, it will be created as a parent itemized line item visible to all audiences.
            </Text>

            <Divider mt={2} mb={2} />

            <Show if={!isLineItemTypesEmpty}>
              <>
                <SimpleReactSelect
                  name={`${prefix}.child_itemized_for`}
                  label="Child Itemized For"
                  options={lineItemAudienceOptions}
                  isMulti
                  isDisabled={isLineItemTypesEmpty}
                />
                <Text fontSize="sm" color="text.muted">
                  Itemized means it will be shown as it's own line item to the selected audience. By default, child line
                  items are always bundled into it's parent. Peli System users will always see line items explicilty. If
                  we want to show line items generated by this markup to a specific audience, enter them here. (EG,
                  Tenants should see their Tenant markups).
                </Text>

                <Divider mt={2} mb={2} />

                <SimpleReactSelect
                  name={`${prefix}.child_excluded_for`}
                  label="Child Excluded For"
                  options={lineItemAudienceOptions}
                  isMulti
                  isDisabled={isLineItemTypesEmpty}
                />
                <Text fontSize="sm" color="text.muted">
                  Excluded means, it won't be bundled into the parent line item. By default, this is empty which means,
                  if it's not itemized, it will be included into the parent line item for all audiences. A scenario we
                  use this is to not show tenant markups bundled into airline freight costs for FPs.
                </Text>
              </>
            </Show>
          </Stack>

          <SimpleInput
            name={`${prefix}.min_cost`}
            label="Min Cost ($)"
            placeholder="0.00"
            type="number"
            inputAttrs={{ step: '0.01' }}
          />
        </Stack>
        <Divider my="0.25em" />
        <TableContainer>
          <Table variant="sensible" size="sm">
            <Thead>
              <Tr>
                <Th>Min Weight</Th>
                <Th>Rate</Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* Keyed by field id: index keys let a removed row's inputs linger on the next row */}
              {fields.map((field, i) => (
                <MarkupCost key={field.id} prefix={`${prefix}.costs.${i}`} onRemove={() => remove(i)} />
              ))}
            </Tbody>
            <Tfoot p="0">
              <Td p="0" colSpan={3} borderBottomRadius="md">
                <Button
                  leftIcon={<HiPlus />}
                  width="full"
                  borderTopRadius="0"
                  justifyContent="flex-start"
                  fontWeight="normal"
                  onClick={() => append({ min: 0, cost: 0 })}
                >
                  Add row
                </Button>
              </Td>
            </Tfoot>
          </Table>
        </TableContainer>
      </AccordionPanel>
    </AccordionItem>
  );
};

/**
 * The `markups` editor for a markup configuration. Expects to be rendered inside a
 * form context whose values include a `markups` array (see `upsertFPC`'s input).
 */
export const MarkupsFieldArray = () => (
  <Accordion allowToggle defaultIndex={0}>
    <SimpleFieldArray
      name="markups"
      addLabel="Add Markup"
      // Blank numeric fields coerce to NaN, which fails validation before the user ever
      // sees the new row — seed everything the schema requires.
      defaultValue={{
        label: '',
        min_cost: 0,
        line_item_types: [],
        costs: [],
        applicable_freight_services: [],
      }}
      form={({ prefix }) => <MarkupForm prefix={prefix as `markups.${number}`} />}
      ignoreStyles
    />
  </Accordion>
);
